import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, Eye, ArrowLeft, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [blog, setBlog] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const viewTracked = React.useRef({});
  
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    comment_text: ''
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadBlog = async () => {
      // Check if we've already tracked a view for this slug
      const shouldTrackView = !viewTracked.current[slug];
      
      if (shouldTrackView) {
        console.log(`[BlogDetail] Fetching blog WITH view tracking: ${slug}`);
        viewTracked.current[slug] = true;
      } else {
        console.log(`[BlogDetail] Fetching blog WITHOUT view tracking: ${slug}`);
      }
      
      try {
        const url = shouldTrackView 
          ? `${API}/blogs/slug/${slug}` 
          : `${API}/blogs/slug/${slug}?skip_view=true`;
        const response = await axios.get(url);
        
        if (isMounted) {
          setBlog(response.data);
          console.log(`[BlogDetail] Blog loaded, views: ${response.data.views_count}`);
        }
      } catch (error) {
        console.error('Failed to fetch blog:', error);
        if (isMounted) {
          navigate('/blogs');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const loadComments = async () => {
      if (isMounted) {
        await fetchComments();
      }
    };

    loadBlog();
    loadComments();
    
    // Show floating button after scroll
    const handleScroll = () => {
      setShowFloatingButton(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      isMounted = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/slug/${slug}?skip_view=true`);
      setBlog(response.data);
    } catch (error) {
      console.error('Failed to fetch blog:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${blog?.id || 'temp'}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/blogs/comments`, {
        blog_id: blog.id,
        ...commentForm
      });
      
      toast({
        title: "Success",
        description: "Your comment has been submitted and is awaiting approval"
      });
      
      setCommentForm({
        author_name: '',
        author_email: '',
        comment_text: ''
      });
    } catch (error) {
      console.error('Failed to submit comment:', error);
      toast({
        title: "Error",
        description: "Failed to submit comment",
        variant: "destructive"
      });
    }
  };

  const handleApplyNowClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
        const nameInput = document.getElementById('name');
        if (nameInput) {
          nameInput.focus();
        }
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p>Loading blog...</p>
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  return (
    <div className="blog-detail-page">
      <div className="container">
        <button onClick={() => navigate('/blogs')} className="back-button">
          <ArrowLeft size={20} />
          <span>Back to Blogs</span>
        </button>

        <article className="blog-article">
          {blog.featured_image && (
            <div className="blog-featured-image">
              <img src={blog.featured_image} alt={blog.title} />
            </div>
          )}

          <header className="blog-article-header">
            <h1 className="heading-1">{blog.title}</h1>
            <div className="blog-meta">
              <span className="blog-meta-item">
                <User size={16} />
                {blog.author}
              </span>
              <span className="blog-meta-item">
                <Calendar size={16} />
                {new Date(blog.published_at || blog.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <span className="blog-meta-item">
                <Eye size={16} />
                {blog.views_count} views
              </span>
            </div>
          </header>

          <div 
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Comments Section */}
          {blog.comments_enabled && (
            <div className="comments-section">
              <h2 className="heading-2">
                <MessageCircle size={24} />
                Comments ({comments.length})
              </h2>

              {/* Comment Form */}
              <form onSubmit={handleCommentSubmit} className="comment-form">
                <h3 className="heading-3">Leave a Comment</h3>
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Your Name *"
                      value={commentForm.author_name}
                      onChange={(e) => setCommentForm({...commentForm, author_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Your Email *"
                      value={commentForm.author_email}
                      onChange={(e) => setCommentForm({...commentForm, author_email: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <textarea
                    className="form-input"
                    placeholder="Your Comment *"
                    value={commentForm.comment_text}
                    onChange={(e) => setCommentForm({...commentForm, comment_text: e.target.value})}
                    rows="4"
                    required
                  />
                </div>
                <button type="submit" className="btn-primary">Submit Comment</button>
                <p className="body-small" style={{ marginTop: '8px', color: '#64748b' }}>
                  Your comment will be reviewed before being published.
                </p>
              </form>

              {/* Comments List */}
              <div className="comments-list">
                {comments.length === 0 ? (
                  <p className="body-medium" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    No comments yet. Be the first to comment!
                  </p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-header">
                        <strong className="body-medium">{comment.author_name}</strong>
                        <span className="body-small" style={{ color: '#94a3b8' }}>
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="body-medium comment-text">{comment.comment_text}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </article>
      </div>

      {/* Floating Apply Now Button */}
      {showFloatingButton && (
        <button className="floating-apply-button" onClick={handleApplyNowClick}>
          Apply Now
        </button>
      )}
    </div>
  );
};

export default BlogDetail;
